<template>
  <div class="tw:bg-blue-100 tw:border tw:border-blue-700 tw:rounded-xl tw:p-4 tw:mt-4">
    <div class="tw:flex tw:items-center tw:mb-3">
      <Bulb class="tw:w-3 tw:h-5" />
      <h4 class="tw:font-bold tw:ml-2">{{ $t('plus_cancel_flow_tips_header') }}</h4>
    </div>

    <div class="tw:flex tw:flex-col tw:gap-3">
      <p v-html="$t(TIPS_SUBTEXT[id])" />

      <ul class="tw:flex tw:flex-col tw:gap-3 tw:list-disc tw:ml-6">
        <li v-for="tip in visibleTips" :key="tip.key">
          <span class="tw:mr-1" v-html="$t(tip.textKey)" />
          <button
            @click="openLink(tip.key, tip.openInNewTab)"
            class="tw:cursor-pointer tw:hover:underline tw:font-bold tw:text-blue-700 tw:hover:text-blue-800;"
          >
            {{ $t(tip.placeholderKey) }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Bulb from '@/components/icons/Bulb.svg?component'
import { platformsLanguages } from '@/constants/platformLanguages.ts'
import { useSessionValues } from '@/composables/useSessionValues'
import appAnalytics from '@/utils/tracking'

const props = defineProps({
  id: String,
  hasOffer: Boolean,
  hasCompetitors: Boolean,
  currentAccommodationId: Number,
  currentOrganisationId: String,
})

const { language } = useSessionValues()

// Constants
const TIPS_SUBTEXT = {
  offseason: 'tip_off_season_header',
  change_of_ownership: 'tip_ownership_header',
  using_another_partner: 'tip_plus_header',
  no_roi: 'tip_plus_header',
  too_expensive: 'tip_budgets_header',
  found_cheaper: 'tip_budgets_header',
  budget_constraints: 'tip_budgets_header',
  dont_understand_features: 'tip_support_header',
  dont_understand_insights: 'tip_support_header',
  no_support: 'tip_support_header',
  doesnt_meet_needs: 'tip_product_fit_header',
  too_complex: 'tip_simplicity_header',
  other: 'tip_plus_header',
}

const ACTIVE_OFFER_IDS = [
  'offseason',
  'using_another_partner',
  'no_roi',
  'too_expensive',
  'found_cheaper',
  'budget_constraints',
  'doesnt_meet_needs',
  'too_complex',
  'other',
]

const RATE_INSIGHTS_IDS = [
  'offseason',
  'using_another_partner',
  'no_roi',
  'too_expensive',
  'found_cheaper',
  'budget_constraints',
  'doesnt_meet_needs',
  'too_complex',
]

const CUSTOMER_SUCCESS_TIPS_IDS = [
  'using_another_partner',
  'no_roi',
  'too_expensive',
  'found_cheaper',
  'budget_constraints',
  'dont_understand_features',
  'dont_understand_insights',
  'no_support',
  'too_complex',
  'other',
]

const VIDEO_IDS = ['dont_understand_insights', 'dont_understand_features', 'no_support']
const GUIDE_IDS = ['dont_understand_insights', 'dont_understand_features', 'no_support']

const GUIDE_FILE_NAMES = {
  'de-DE': '/home/BusinessStudioPlusGuide_de.pdf',
  'it-IT': '/home/BusinessStudioPlusGuide_it.pdf',
  'fr-FR': '/home/BusinessStudioPlusGuide_fr.pdf',
  'es-ES': '/home/BusinessStudioPlusGuide_es.pdf',
  'es-MX': '/home/BusinessStudioPlusGuide_mx.pdf',
  'pt-BR': '/home/BusinessStudioPlusGuide_br.pdf',
  'pt-PT': '/home/BusinessStudioPlusGuide_pt.pdf',
  'el-GR': '/home/BusinessStudioPlusGuide_el.pdf',
  'tr-TR': '/home/BusinessStudioPlusGuide_tr.pdf',
}

const TIPS_CONFIG = [
  {
    key: 'propertyOnTrivago',
    textKey: 'tip_contact_badge',
    placeholderKey: 'tip_contct_badge_placeholder',
    openInNewTab: true,
  },
  {
    key: 'noActiveOffer',
    textKey: 'tip_hotel_offer',
    placeholderKey: 'tip_hotel_offer_placeholder',
    openInNewTab: false,
  },
  {
    key: 'hasActiveOffer',
    textKey: 'tip_hotel_offer_tip_2',
    placeholderKey: 'tip_hotel_offer_2_placeholder',
    openInNewTab: true,
  },
  {
    key: 'rateInsightsCompetitors',
    textKey: 'tip_rate_insights',
    placeholderKey: 'tip_rate_insights_placeholder',
    openInNewTab: false,
  },
  {
    key: 'rateInsights',
    textKey: 'tip_rate_insights_tip_2',
    placeholderKey: 'tip_rate_insights_placeholder',
    openInNewTab: false,
  },
  {
    key: 'visitorsProfile',
    textKey: 'tip_visitors_profile',
    placeholderKey: 'tip_visitors_profile_placeholder',
    openInNewTab: false,
  },
  {
    key: 'video',
    textKey: 'tip_plus_video',
    placeholderKey: 'tip_plus_video_placeholder',
    openInNewTab: true,
  },
  {
    key: 'guides',
    textKey: 'tip_plus_guide',
    placeholderKey: 'tip_plus_guide_placeholder',
    openInNewTab: true,
  },
  {
    key: 'customerSuccessTips',
    textKey: 'tip_customer_success',
    placeholderKey: 'tip_customer_success_placeholder',
    openInNewTab: true,
  },
  {
    key: 'customerSuccessProcess',
    textKey: 'tip_customer_success_tip_2',
    placeholderKey: 'tip_customer_success_placeholder',
    openInNewTab: true,
  },
]

// Computed properties
const guideFileName = computed(() => {
  return GUIDE_FILE_NAMES[language.value] ?? '/home/BusinessStudioPlusGuide.pdf'
})

const elementVisibility = computed(() => ({
  propertyOnTrivago: props.id === 'offseason',
  noActiveOffer: ACTIVE_OFFER_IDS.includes(props.id) && !props.hasOffer,
  hasActiveOffer: ACTIVE_OFFER_IDS.includes(props.id) && props.hasOffer,
  rateInsightsCompetitors: RATE_INSIGHTS_IDS.includes(props.id) && !props.hasCompetitors,
  rateInsights: RATE_INSIGHTS_IDS.includes(props.id) && props.hasCompetitors,
  visitorsProfile: props.id === 'doesnt_meet_needs',
  customerSuccessTips: CUSTOMER_SUCCESS_TIPS_IDS.includes(props.id),
  customerSuccessProcess: props.id === 'change_of_ownership',
  guides: GUIDE_IDS.includes(props.id),
  video: VIDEO_IDS.includes(props.id),
}))

const links = computed(() => ({
  propertyOnTrivago: `https://www.trivago.com/${platformsLanguages[language.value]}/srl?search=100-${props.currentAccommodationId}`,
  noActiveOffer: `/home/hotel-offers?itemID=${props.currentAccommodationId}&orgID=${props.currentOrganisationId}`,
  hasActiveOffer: `https://www.trivago.com/${platformsLanguages[language.value]}/srl?search=100-${props.currentAccommodationId}`,
  rateInsightsCompetitors: `/home/rate-insights?itemID=${props.currentAccommodationId}&orgID=${props.currentOrganisationId}`,
  rateInsights: `/home/rate-insights?itemID=${props.currentAccommodationId}&orgID=${props.currentOrganisationId}`,
  visitorsProfile: `/home/visitors-profile?itemID=${props.currentAccommodationId}&orgID=${props.currentOrganisationId}`,
  customerSuccessTips:
    'https://outlook.office365.com/book/TrivagoBussinessPlusHelpmeupgradetoPlus@trvoffice.onmicrosoft.com/',
  customerSuccessProcess:
    'https://outlook.office365.com/book/TrivagoBussinessPlusHelpmeupgradetoPlus@trvoffice.onmicrosoft.com/',
  video: 'https://www.businessblog-trivago.com/videos',
  guides: guideFileName.value,
}))

const visibleTips = computed(() => {
  return TIPS_CONFIG.filter(tip => elementVisibility.value[tip.key])
})

// Methods
const openLink = (section, isBlank) => {
  const trackingData = {
    item_id: props.currentAccommodationId,
    reason_id: props.id,
    name: section,
  }
  if (section === 'guides') {
    trackingData.guide = guideFileName.value?.split('/').pop()
  }

  appAnalytics.track(appAnalytics.events.SP_SMART_TIPS_LINK_CLICKED, trackingData)

  const url = links.value[section]

  if (isBlank) {
    window.open(url, '_blank')
  } else {
    window.location.href = url
  }
}
</script>
